import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import BackupIcon from '@mui/icons-material/Backup';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from '../components/common/PageHeader';
import {
  BackupItem,
  backupsService,
} from '../services/backupsService';

const formatSize = (bytes: number | null): string => {
  if (bytes == null) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (ts: string | null): string => {
  if (!ts) return '–';
  try {
    const d = new Date(ts);
    return d.toLocaleString('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return ts;
  }
};

const BackupPage: React.FC = () => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState<'local' | 'storage_box' | 'both'>('both');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [localOnly, setLocalOnly] = useState(false);
  const [includeLogs, setIncludeLogs] = useState(false);

  const loadBackups = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await backupsService.list(source);
      setBackups(data.backups);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Backups konnten nicht geladen werden';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  useEffect(() => {
    if (!jobId || jobStatus === 'success' || jobStatus.startsWith('error') || jobStatus === 'finished') return;
    const t = setInterval(async () => {
      try {
        const s = await backupsService.getStatus(jobId);
        setJobStatus(s.status);
        if (s.status === 'success' || s.status === 'finished') {
          loadBackups();
        }
      } catch {
        setJobStatus('unknown');
      }
    }, 2000);
    return () => clearInterval(t);
  }, [jobId, jobStatus, loadBackups]);

  const handleTrigger = async () => {
    try {
      setTriggering(true);
      setError('');
      const res = await backupsService.trigger(localOnly, includeLogs);
      setJobId(res.job_id);
      setJobStatus(res.status);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Backup konnte nicht gestartet werden';
      setError(detail);
    } finally {
      setTriggering(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Backups"
        subtitle="Backups auflisten und manuell erstellen"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Backups' }]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Backup jetzt erstellen
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={triggering ? <CircularProgress size={18} color="inherit" /> : <BackupIcon />}
              onClick={handleTrigger}
              disabled={triggering}
            >
              {triggering ? 'Wird erstellt…' : 'Backup erstellen'}
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={localOnly}
                  onChange={(e) => setLocalOnly(e.target.checked)}
                  color="primary"
                />
              }
              label="Nur lokal (kein Storage-Box-Upload)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeLogs}
                  onChange={(e) => setIncludeLogs(e.target.checked)}
                  color="primary"
                />
              }
              label="Logs mit sichern"
            />
            {jobStatus && (
              <Tooltip
                title={jobStatus.startsWith('error') ? jobStatus.replace(/^error:\s*/, '') : ''}
                placement="top"
              >
                <Chip
                  label={
                    jobStatus === 'running'
                      ? 'Backup läuft…'
                      : jobStatus === 'success' || jobStatus === 'finished'
                      ? 'Erfolgreich'
                      : jobStatus.startsWith('error')
                      ? 'Fehler'
                      : jobStatus
                  }
                  color={
                    jobStatus === 'success' || jobStatus === 'finished'
                      ? 'success'
                      : jobStatus.startsWith('error')
                      ? 'error'
                      : 'default'
                  }
                  size="small"
                />
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Verfügbare Backups
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadBackups}
                disabled={loading}
              >
                Aktualisieren
              </Button>
              <Chip
                label={source}
                size="small"
                onClick={() =>
                  setSource((s) =>
                    s === 'both' ? 'local' : s === 'local' ? 'storage_box' : 'both'
                  )
                }
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : backups.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3 }}>
              Keine Backups gefunden. Erstellen Sie ein Backup über den Button oben.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dateiname</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell align="right">Größe</TableCell>
                    <TableCell>Quelle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backups.map((b) => (
                    <TableRow key={b.filename}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            wordBreak: 'break-all',
                          }}
                        >
                          {b.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(b.timestamp)}</TableCell>
                      <TableCell align="right">{formatSize(b.size)}</TableCell>
                      <TableCell>
                        <Chip
                          label={b.source}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: b.source === 'storage_box' ? accent : undefined,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BackupPage;
